import { Add } from "@mui/icons-material"
import { Box, Button, Card, styled, Typography } from "@mui/material"
import FlexBetween from "components/flexbox/FlexBetween"
import FlexBox from "components/flexbox/FlexBox"
import SearchInput from "components/input-fields/SearchInput"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import apiService from "Network/apiService"
import { API_ENDPOINTS } from "Network/apiEndpoints"
import { searchByName } from "utils/utils"
import { tableData2 } from "__fakeData__/dataTable" // styled components
import AddChapterModal from "page-sections/Chapters-data-table/AddChapterModal"
import ChaptersDataTable from "page-sections/Chapters-data-table"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CircularProgress } from "@mui/material";

const ButtonWrapper = styled(FlexBox)(({ theme }) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column",
    },
    "& .MuiButton-root": {
      minWidth: "100%",
    },
  },
}))

const DatePickerWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "& .react-datepicker-wrapper": {
      width: "130px"
    },
    "& .react-datepicker__input-container input": {
      padding: "8px 12px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      width: "100%",
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:hover': {
        borderColor: theme.palette.primary.main
      },
      '&:focus': {
        borderColor: theme.palette.primary.main,
        outline: 'none',
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
      }
    },
    "& .react-datepicker": {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontFamily: theme.typography.fontFamily,
      "& .react-datepicker__header": {
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "& .react-datepicker__current-month": {
          color: theme.palette.text.primary,
          fontSize: "0.875rem",
          fontWeight: theme.typography.fontWeightMedium
        },
        "& .react-datepicker__day-name": {
          color: theme.palette.text.secondary
        }
      },
      "& .react-datepicker__day": {
        color: theme.palette.text.primary,
        "&:hover": {
          backgroundColor: theme.palette.action.hover
        },
        "&.react-datepicker__day--selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark
          }
        },
        "&.react-datepicker__day--keyboard-selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        },
        "&.react-datepicker__day--disabled": {
          color: theme.palette.text.disabled
        }
      },
      "& .react-datepicker__triangle": {
        display: "none"
      }
    }
  }));


  const SummaryText = styled(Typography)(({ theme }) => ({
    margin: '24px 0',
    padding: '16px 24px',
    background: `linear-gradient(to right, ${theme.palette.primary.light}15, ${theme.palette.background.paper})`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 400,
    display: 'block',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
      transform: 'translateY(-1px)',
    },
    '& .date': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: -2,
        left: 0,
        width: '100%',
        height: '2px',
        background: `${theme.palette.primary.main}25`,
      }
    }
  }));

const Chapters = ({ onDataFetch }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { regionId, regionName } = location.state || {}
  
  const [chapters, setChapters] = useState([])
  const [searchValue, setSearchValue] = useState("")
  const [openModal, setOpenModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)))
  const [endDate, setEndDate] = useState(new Date())

  const fetchChapterSummary = async () => {
    if (!regionId) return
    
    setIsLoading(true)
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0]
      const formattedEndDate = endDate.toISOString().split('T')[0]
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_chapter_summery}?region_id=${regionId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      )
      
      if (response?.success) {
        setChapters(response.data.chapters || [])
        onDataFetch?.(response)
      }
    } catch (error) {
      console.error("Error fetching chapter summary:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchChapterSummary()
  }, [regionId, startDate, endDate])

  const filteredChapters = chapters.filter(chapter => 
    chapter.chapter_name.toLowerCase().includes(searchValue.toLowerCase())
  )

  return (
    <Box pt={2} pb={4}>
      {regionName && (
        <Typography variant="h5" mb={2}>
          Chapters in {regionName}
        </Typography>
      )}
      
      <FlexBetween flexWrap="wrap">
        <FlexBox alignItems="center" gap={3}>
          <SearchInput bordered={false} placeholder="Find Chapters" onChange={(e) => setSearchValue(e.target.value)} />
          
          <DatePickerWrapper>
            <DatePicker
              selected={startDate}
              onChange={date => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
              placeholderText="Start Date"
              dateFormat="MM/dd/yyyy"
            />
            <DatePicker
              selected={endDate}
              onChange={date => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              placeholderText="End Date"
              dateFormat="MM/dd/yyyy"
            />
          </DatePickerWrapper>
        </FlexBox>

        <ButtonWrapper alignItems="center">
          <Button variant="contained" size="small" endIcon={<Add />} onClick={() => setOpenModal(true)}>
            {t("Add Chapter")}
          </Button>
        </ButtonWrapper>
      </FlexBetween>

      <AddChapterModal open={openModal} onClose={() => setOpenModal(false)} onSuccess={fetchChapterSummary} region={regionId} />

      <SummaryText>
        Summary of Chapters from{' '}
        <span className="date">
          {startDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>{' '}
        to{' '}
        <span className="date">
          {endDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>
      </SummaryText>
      <Card sx={{ marginTop: 3, minHeight: 300, position: 'relative' }}>
        {!regionId ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Typography color="text.secondary">
              Please select a region to view chapters
            </Typography>
          </Box>
        ) : (
          <ChaptersDataTable
            data={filteredChapters}
            isLoading={isLoading}
            onRefresh={fetchChapterSummary}
          />
        )}
      </Card>
    </Box>
  )
}

export default Chapters

